import { createRoot } from 'react-dom/client';
import '../../../libs/ui/styles/src/reset.css';
import initializeSentry from '@apply/libs/sentry/initializeSentry';
import App from './app/App';
import { ENV } from '@core/constants/env';
import { isApplicationEmpty } from '@core/infrastructure/mocks/helpers';

const root = createRoot(document.getElementById('root') as HTMLElement);

async function start() {
    // Initialize Autofill Callback Handler
    window._AutofillCallbackHandler =
        window._AutofillCallbackHandler || function () {};

    if (ENV.MODE !== 'prod') {
        const [
            { worker },
            { mocksEnabled },
            { registerGql },
            { registerHTTP },
            {
                borrowerLoginGqlStub,
                mfaChallengeGqlStub,
                mfaLoginGqlStub,
                sendResetPasswordLinkGqlStub,
                checkResetTokenGqlStub,
                setNewPasswordGqlStub,
            },
            { borrowerInfoGqlStub },
            {
                getApplicationStubIncomplete,
                getApplicationStubEmpty,
                submitApplicationStub,
                getApplicationStatus,
                exchangeTokenStub,
            },
            { getOffersStub },
            { createAppStub, createAppRepeatStub },
            { setPartStub },
            { findRepeatBorrowerStub },
            { ipTokenStub },
            { getC2CStub },
            { getAllAgreementsStub },
            { signDynamicAgreementStub },
            { issueLoanStub },
            { dictionariesStubsGql },
            { pvSubmitPhoneStub },
            { pvVerifyOtpStub },
            {
                getLeadDataStub,
                getLeadDataJWTStub,
                createLeadApplicationStub,
                submitLeadApplicationStub,
                expireLeadStub,
            },
        ] = await Promise.all([
            import('@core/infrastructure/mocks/msw-worker'),
            import('@core/infrastructure/mocks/shim'),
            import('@core/infrastructure/mocks/adapters/graphql'),
            import('@core/infrastructure/mocks/adapters/http'),
            import('@core/auth/__stubs__/auth-gql-stubs'),
            import('@core/auth/__stubs__/borrowerInfoStub'),
            import('@apply/api/__stubs__/application-stubs'),
            import('@apply/api/__stubs__/getOffers-stub'),
            import('@apply/api/__stubs__/createApp-stubs'),
            import('@apply/api/__stubs__/setPart-stub'),
            import('@apply/api/__stubs__/findRepeatBorrower-stub'),
            import('@apply/api/__stubs__/ipToken-stub'),
            import('@apply/api/__stubs__/getC2C-stub'),
            import('@apply/api/__stubs__/getAllAgreements-stub'),
            import('@apply/api/__stubs__/signDynamicAgreement-stub'),
            import('@apply/api/__stubs__/issueLoan-stub'),
            import('@apply/api/__stubs__/dictionaries-stub'),
            import('@apply/api/__stubs__/pvSubmitPhone-stub'),
            import('@apply/api/__stubs__/pvVerifyOtp-stub'),
            import('@apply/api/__stubs__/lead-stubs'),
        ]);
        if (mocksEnabled()) {
            console.warn('Mocks are enabled');
            await worker.start();
            registerHTTP(ipTokenStub);
            registerGql(borrowerLoginGqlStub);
            registerGql(mfaChallengeGqlStub);
            registerGql(mfaLoginGqlStub);
            registerGql(sendResetPasswordLinkGqlStub);
            registerGql(checkResetTokenGqlStub);
            registerGql(setNewPasswordGqlStub);
            registerGql(borrowerInfoGqlStub);
            registerGql(submitApplicationStub);
            registerGql(
                isApplicationEmpty()
                    ? getApplicationStubEmpty
                    : getApplicationStubIncomplete
            );
            registerGql(getApplicationStatus);
            registerGql(exchangeTokenStub);
            registerGql(getOffersStub);
            registerGql(createAppStub);
            registerGql(setPartStub);
            registerGql(findRepeatBorrowerStub);
            registerGql(getC2CStub);
            registerGql(getAllAgreementsStub);
            registerGql(signDynamicAgreementStub);
            registerGql(issueLoanStub);
            registerGql(dictionariesStubsGql);
            registerGql(createAppRepeatStub);
            registerGql(pvSubmitPhoneStub);
            registerGql(pvVerifyOtpStub);
            registerGql(getLeadDataStub);
            registerGql(getLeadDataJWTStub);
            registerGql(createLeadApplicationStub);
            registerGql(submitLeadApplicationStub);
            registerGql(expireLeadStub);
        }
    }

    root.render(<App />);
}

initializeSentry();
start();
